import { useEffect, useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { api } from '@/lib/api';
import { useOutlets } from '@/hooks/useOutlets';
import { endOfDay, format, startOfDay } from 'date-fns';
import type { RevenueReport } from '@photobooth/types';

export function RevenuePage() {
  const { outlets } = useOutlets();
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [selectedOutletId, setSelectedOutletId] = useState('');
  const [report, setReport] = useState<RevenueReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [revenueByOutletSort, setRevenueByOutletSort] = useState<{
    by: 'outlet' | 'total' | 'session' | 'addons';
    order: 'asc' | 'desc';
  }>({ by: 'total', order: 'desc' });
  const [revenueByProductSort, setRevenueByProductSort] = useState<{
    by: 'product' | 'quantity' | 'revenue';
    order: 'asc' | 'desc';
  }>({ by: 'revenue', order: 'desc' });

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const response = await api.reports.getRevenueReport({
        // Use full-day inclusive range for the selected dates
        startDate: startOfDay(new Date(dateRange.startDate)),
        endDate: endOfDay(new Date(dateRange.endDate)),
        outletId: selectedOutletId || undefined,
      });
      if (response.success && response.data) {
        const reportData = (response.data as any).data ?? response.data;
        setReport(reportData);
      }
    } catch (err) {
      alert('Failed to fetch revenue report');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  // Revenue by product: sorted rows
  const productBreakdown = report?.breakdown ?? [];
  const sortedProductBreakdown = [...productBreakdown].sort((a, b) => {
    const mult = revenueByProductSort.order === 'asc' ? 1 : -1;
    const qtyA = a.transactionCount ?? 0;
    const qtyB = b.transactionCount ?? 0;
    const revA = a.totalAmount ?? 0;
    const revB = b.totalAmount ?? 0;
    switch (revenueByProductSort.by) {
      case 'product':
        return mult * (a.productName ?? '').localeCompare(b.productName ?? '');
      case 'quantity':
        return mult * (qtyA - qtyB);
      case 'revenue':
        return mult * (revA - revB);
      default:
        return 0;
    }
  });

  const handleExportRevenueByProductPDF = () => {
    const doc = new jsPDF();
    const periodText = `Period: ${format(new Date(dateRange.startDate), 'dd MMM yyyy')} to ${format(new Date(dateRange.endDate), 'dd MMM yyyy')}`;

    doc.setFontSize(18);
    doc.text('Revenue by Product', 14, 20);
    doc.setFontSize(10);
    doc.text(periodText, 14, 28);

    autoTable(doc, {
      startY: 35,
      head: [['Product', 'Quantity', 'Price', 'Revenue']],
      body: sortedProductBreakdown.map((item) => {
        const qty = item.transactionCount ?? 0;
        const price = qty > 0 ? Math.round((item.totalAmount ?? 0) / qty) : 0;
        return [
          item.productName ?? '',
          String(qty),
          `Rp ${price.toLocaleString()}`,
          `Rp ${(item.totalAmount ?? 0).toLocaleString()}`,
        ];
      }),
      theme: 'grid',
      headStyles: { fillColor: [100, 100, 100] },
    });

    doc.save(`revenue-by-product-${dateRange.startDate}-${dateRange.endDate}.pdf`);
  };

  // Revenue by outlet: rows = all outlets, cols = Session | Add-ons | Total (with product breakdown)
  const outletRevenueRows: Array<{
    outletId: string;
    outletName: string;
    sessionAmount: number;
    addonsAmount: number;
    totalAmount: number;
    sessionProducts: Array<{ productName: string; amount: number }>;
    addonsProducts: Array<{ productName: string; amount: number }>;
  }> = (() => {
    const contributions = report?.contributions ?? [];
    const outletMap = new Map<
      string,
      {
        outletId: string;
        outletName: string;
        sessionAmount: number;
        addonsAmount: number;
        sessionProducts: Map<string, number>;
        addonsProducts: Map<string, number>;
      }
    >();

    for (const c of contributions) {
      if (!outletMap.has(c.outletId)) {
        outletMap.set(c.outletId, {
          outletId: c.outletId,
          outletName: c.outletName,
          sessionAmount: 0,
          addonsAmount: 0,
          sessionProducts: new Map(),
          addonsProducts: new Map(),
        });
      }
      const entry = outletMap.get(c.outletId)!;
      const amt = c.totalAmount ?? 0;
      const productName = c.productName ?? 'Unknown';
      if (c.productType === 'addon') {
        entry.addonsAmount += amt;
        entry.addonsProducts.set(productName, (entry.addonsProducts.get(productName) ?? 0) + amt);
      } else {
        entry.sessionAmount += amt;
        entry.sessionProducts.set(productName, (entry.sessionProducts.get(productName) ?? 0) + amt);
      }
    }

    // Include all outlets (from useOutlets), including those with no revenue
    return outlets.map((o) => {
      const data = outletMap.get(o.outletId);
      const sessionAmount = data?.sessionAmount ?? 0;
      const addonsAmount = data?.addonsAmount ?? 0;
      const sessionProducts = data
        ? Array.from(data.sessionProducts.entries())
            .map(([productName, amount]) => ({ productName, amount }))
            .sort((a, b) => a.productName.localeCompare(b.productName))
        : [];
      const addonsProducts = data
        ? Array.from(data.addonsProducts.entries())
            .map(([productName, amount]) => ({ productName, amount }))
            .sort((a, b) => a.productName.localeCompare(b.productName))
        : [];
      return {
        outletId: o.outletId,
        outletName: o.name,
        sessionAmount,
        addonsAmount,
        totalAmount: sessionAmount + addonsAmount,
        sessionProducts,
        addonsProducts,
      };
    });
  })();

  const totalsRow = outletRevenueRows.reduce(
    (acc, row) => ({
      sessionAmount: acc.sessionAmount + row.sessionAmount,
      addonsAmount: acc.addonsAmount + row.addonsAmount,
      totalAmount: acc.totalAmount + row.totalAmount,
    }),
    { sessionAmount: 0, addonsAmount: 0, totalAmount: 0 }
  );

  const sortedOutletRevenueRows = [...outletRevenueRows].sort((a, b) => {
    const mult = revenueByOutletSort.order === 'asc' ? 1 : -1;
    switch (revenueByOutletSort.by) {
      case 'outlet':
        return mult * a.outletName.localeCompare(b.outletName);
      case 'total':
        return mult * (a.totalAmount - b.totalAmount);
      case 'session':
        return mult * (a.sessionAmount - b.sessionAmount);
      case 'addons':
        return mult * (a.addonsAmount - b.addonsAmount);
      default:
        return 0;
    }
  });

  const handleExportRevenueByOutletPDF = () => {
    const doc = new jsPDF();
    const periodText = `Period: ${format(new Date(dateRange.startDate), 'dd MMM yyyy')} to ${format(new Date(dateRange.endDate), 'dd MMM yyyy')}`;

    doc.setFontSize(18);
    doc.text('Revenue by Outlet', 14, 20);
    doc.setFontSize(10);
    doc.text(periodText, 14, 28);

    const formatSessionCell = (row: (typeof outletRevenueRows)[0]) => {
      const subtotal = `Rp ${row.sessionAmount.toLocaleString()}`;
      if (row.sessionProducts.length === 0) return subtotal;
      const breakdown = row.sessionProducts.map((p) => `${p.productName}: Rp ${p.amount.toLocaleString()}`).join('\n');
      return `${subtotal}\n${breakdown}`;
    };
    const formatAddonsCell = (row: (typeof outletRevenueRows)[0]) => {
      const subtotal = `Rp ${row.addonsAmount.toLocaleString()}`;
      if (row.addonsProducts.length === 0) return subtotal;
      const breakdown = row.addonsProducts.map((p) => `${p.productName}: Rp ${p.amount.toLocaleString()}`).join('\n');
      return `${subtotal}\n${breakdown}`;
    };

    autoTable(doc, {
      startY: 35,
      head: [['Outlet', 'Session', 'Add-ons', 'Total']],
      body: [
        ...sortedOutletRevenueRows.map((row) => [
          row.outletName,
          formatSessionCell(row),
          formatAddonsCell(row),
          `Rp ${row.totalAmount.toLocaleString()}`,
        ]),
        [
          'Total',
          `Rp ${totalsRow.sessionAmount.toLocaleString()}`,
          `Rp ${totalsRow.addonsAmount.toLocaleString()}`,
          `Rp ${totalsRow.totalAmount.toLocaleString()}`,
        ],
      ],
      theme: 'grid',
      headStyles: { fillColor: [100, 100, 100] },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 55 },
        2: { cellWidth: 55 },
        3: { cellWidth: 35 },
      },
    });

    doc.save(`revenue-by-outlet-${dateRange.startDate}-${dateRange.endDate}.pdf`);
  };

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-text">Revenue Reports</h1>
        <p className="text-text-muted mt-1">Track sales and revenue across all outlets</p>
      </div>

      <div className="bg-surface rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-bold text-text mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Outlet</label>
            <select
              value={selectedOutletId}
              onChange={(e) => setSelectedOutletId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Outlets</option>
              {outlets.map((outlet) => (
                <option key={outlet.outletId} value={outlet.outletId}>{outlet.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchReport}
              disabled={isLoading}
              className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-surface p-6 rounded-lg shadow">
              <h3 className="text-text-muted text-sm font-medium">Total Revenue</h3>
              <p className="text-3xl font-bold text-text mt-2">
                Rp {(report.totalAmount ?? 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-surface p-6 rounded-lg shadow">
              <h3 className="text-text-muted text-sm font-medium">Total Transactions</h3>
              <p className="text-3xl font-bold text-text mt-2">{report.transactionCount ?? 0}</p>
            </div>
            <div className="bg-surface p-6 rounded-lg shadow">
              <h3 className="text-text-muted text-sm font-medium">Average Order Value</h3>
              <p className="text-3xl font-bold text-text mt-2">
                Rp {((report.totalAmount ?? 0) / (report.transactionCount || 1)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
            </div>
          </div>

          <div className="bg-surface rounded-lg shadow p-6 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <h2 className="text-xl font-bold text-text">Revenue by Product</h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label htmlFor="product-sort" className="text-sm text-text-muted">Sort by:</label>
                  <select
                    id="product-sort"
                    value={`${revenueByProductSort.by}-${revenueByProductSort.order}`}
                    onChange={(e) => {
                      const [by, order] = e.target.value.split('-') as ['product' | 'quantity' | 'revenue', 'asc' | 'desc'];
                      setRevenueByProductSort({ by, order });
                    }}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="revenue-desc">Revenue (high to low)</option>
                    <option value="revenue-asc">Revenue (low to high)</option>
                    <option value="product-asc">Product name (A–Z)</option>
                    <option value="product-desc">Product name (Z–A)</option>
                    <option value="quantity-desc">Quantity (high to low)</option>
                    <option value="quantity-asc">Quantity (low to high)</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleExportRevenueByProductPDF}
                  className="text-primary hover:underline text-sm font-medium"
                >
                  Export to PDF
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">Revenue</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedProductBreakdown.map((item) => (
                    <tr key={item.productId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text">{item.productName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">{item.transactionCount ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                        Rp {(
                          (item.transactionCount ?? 0) > 0
                            ? Math.round((item.totalAmount ?? 0) / (item.transactionCount ?? 1))
                            : 0
                        ).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                        Rp {(item.totalAmount ?? 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-surface rounded-lg shadow p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <h2 className="text-xl font-bold text-text">Revenue by Outlet</h2>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <label htmlFor="revenue-sort" className="text-sm text-text-muted">Sort by:</label>
                      <select
                        id="revenue-sort"
                        value={`${revenueByOutletSort.by}-${revenueByOutletSort.order}`}
                        onChange={(e) => {
                          const [by, order] = e.target.value.split('-') as ['outlet' | 'total' | 'session' | 'addons', 'asc' | 'desc'];
                          setRevenueByOutletSort({ by, order });
                        }}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                      >
                        <option value="total-desc">Total (high to low)</option>
                        <option value="total-asc">Total (low to high)</option>
                        <option value="outlet-asc">Outlet (A–Z)</option>
                        <option value="outlet-desc">Outlet (Z–A)</option>
                        <option value="session-desc">Session subtotal (high to low)</option>
                        <option value="session-asc">Session subtotal (low to high)</option>
                        <option value="addons-desc">Add-ons subtotal (high to low)</option>
                        <option value="addons-asc">Add-ons subtotal (low to high)</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={handleExportRevenueByOutletPDF}
                      className="text-primary hover:underline text-sm font-medium"
                    >
                      Export to PDF
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                          Outlet
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">
                          Session
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">
                          Add-ons
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedOutletRevenueRows.map((row) => (
                        <tr key={row.outletId}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text">
                            {row.outletName}
                          </td>
                          <td className="px-6 py-4 text-sm text-text-muted text-right align-top">
                            <div className="font-medium text-text mb-1">
                              Rp {row.sessionAmount.toLocaleString()}
                            </div>
                            {row.sessionProducts.length > 0 ? (
                              <ul className="space-y-1 text-xs">
                                {row.sessionProducts.map((p) => (
                                  <li key={p.productName}>
                                    <span className="text-text">{p.productName}:</span>{' '}
                                    Rp {p.amount.toLocaleString()}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </td>
                          <td className="px-6 py-4 text-sm text-text-muted text-right align-top">
                            <div className="font-medium text-text mb-1">
                              Rp {row.addonsAmount.toLocaleString()}
                            </div>
                            {row.addonsProducts.length > 0 ? (
                              <ul className="space-y-1 text-xs">
                                {row.addonsProducts.map((p) => (
                                  <li key={p.productName}>
                                    <span className="text-text">{p.productName}:</span>{' '}
                                    Rp {p.amount.toLocaleString()}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-text text-right">
                            Rp {row.totalAmount.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text">Total</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted text-right">
                          Rp {totalsRow.sessionAmount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted text-right">
                          Rp {totalsRow.addonsAmount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text text-right">
                          Rp {totalsRow.totalAmount.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
          </div>
        </>
      )}
    </div>
  );
}
